import React, { useEffect, useRef, useState } from "react";

import BadgeofEthicsSvg from "images/Badge-of-Ethics-02.inline.svg";
import logo from "images/gvi-logo2.svg";
import ArrowRight from "images/icons/arrow-right.inline.svg";
import FacebookIcon from "images/icons/facebook-sm.inline.svg";
import downloadSvg from "images/icons/fi-rr-download.svg";
import GoogleScholarLogo from "images/icons/google-scholar-logo.svg";
import InstaIcon from "images/icons/insta-sm.inline.svg";
import LinkIcon from "images/icons/linked-sm.inline.svg";
import PhoneIcon from "images/icons/phone-icon.inline.svg";
import TiktokIcon from "images/icons/tiktok-sm.inline.svg";
import YoutubeIcon from "images/icons/youtube-sm.inline.svg";

import { useBannerDetails } from "../hooks/useBannerManager";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { useProgramFees } from "../hooks/useProgramFees";
import { Link } from "../utils/customLink";
import { flattenMenu } from "../utils/flattenMenu";
import { shortcodePreProcessor } from "../utils/formatContent";
import Image from "./image";
import BlogStickyFooter from "./shared/sticky/blogStickyFooter";
import SiteSwitcherPopup from "./widgets/siteSwitcherPopup";

const SocialLink = ({ url, icon, altName }) => {
  return (
    <a
      className=" w-10 h-10 bg-white dark:bg-gray-darkest rounded-full flex p-2 ml-0.5 sm:w-9 sm:h-9 cursor-pointer"
      href={url ?? "#"}
      target={"_blank"}
      rel="noreferrer"
      title={icon}
    >
      {icon}
    </a>
  );
};

const DownloadButton = ({
  label,
  url,
  imgSource = downloadSvg,
  imgClass = ""
}) => {
  return (
    <div className="relative flex items-center justify-center w-full border-2 border-white rounded-sm">
      <a
        href={url}
        target={"_gviblank"}
        rel="noreferrer"
        title={label}
        className="flex flex-row items-center justify-center w-full py-2 mx-auto text-base font-bold text-white cursor-pointer"
      >
        {label}
        <Image
          imageAlt={"Download button"}
          image={""}
          imageClass={`h-4 px-4 ${imgClass}`}
          sourceUrl={imgSource}
        />
      </a>
    </div>
  );
};

/**
 * current break point < sm: Accordion
 * current break point > sm: General link list
 */
const SubAccordionList = ({ data, className }) => {
  const [isOpened, setOpened] = useState(false);
  const [height, setHeight] = useState("0px");
  const contentElement = useRef(null);
  const breakpoint = useBreakpoint();
  const handleAccordion = () => {
    setOpened(!isOpened);
    setHeight(!isOpened ? `${contentElement.current.scrollHeight}px` : "0px");
  };
  return (
    <div className={className}>
      <div
        className={"flex justify-between text-white items-center"}
        onClick={breakpoint === "xs" ? handleAccordion : undefined}
      >
        <p className="text-base font-semibold text-white">{data.title}</p>
        <ArrowRight
          className={`visible w-3 h-3 stroke-white sm:invisible ${
            isOpened ? "-rotate-90" : "rotate-90"
          }`}
        />
      </div>
      <div className="w-full h-0 my-2 border-b border-gray-testimonial sm:hidden" />

      <div
        ref={contentElement}
        style={{ height: breakpoint === "xs" ? height : "" }}
        className="overflow-hidden transition-all duration-200"
      >
        <div className="pb-5 sm:pb-0">
          {data.children &&
            data.children.map((item, index) => (
              <Link
                className="block py-1 text-xs text-white dark:text-gray-darken"
                to={item.path ?? item.url}
                key={index}
              >
                {item.label ?? item.node}
              </Link>
            ))}
        </div>
      </div>
    </div>
  );
};

const Footer = ({
  site,
  footerMenu,
  isMobile = false,
  brandingOpts,
  globalFileLinks,
  enquireNow,
  applyNow,
  showProgramPrice = false,
  showPricingStickyWidget = false,
  programPrice,
  programType = [],
  showPromotion = false,
  programPromotion = "",
  additionalFooterContainerClass = "",
  updateOnScroll = true,
  stickyFooterDetails = {},
  showMobileEnquireNow = true,
  showMobileApplyNow = true,
  showMobileStickyFooter = true,
  showGrantsBanner = true         
}) => {
  // do not show the mobile footer float on forms
  const [isForm, setIsFormPage] = useState(false);
  const [applyNowUrl, setApplyNowUrl] = useState("/application-form/");
  const [enquireUrl, setEnquireUrl] = useState("/enquiry-form/");
  const [currentGroupTravelURL, setCurrentGroupTravelURL] = useState("");
  const [banner, setBanner] = useState({});

  // eslint-disable-next-line
  const { social_media, brochures, sec_nav_title } = site?.siteMetadata ?? {
    social_media: {},
    brochures: {},
    sec_nav_title: ""
  };

  const { currentBrand, currentSiteMeta, planetDomain, peopleDomain } =
    brandingOpts ?? {
      currentBrand: "",
      currentSiteMeta: {},
      planetDomain: "",
      peopleDomain: ""
    };

  const contactAddress =
    currentSiteMeta?.contact_details?.address?.join("") ?? "";
  const intlNumber = currentSiteMeta?.contact_details?.international ?? "";
  const secondarySiteUrl = currentBrand.includes("people")
    ? planetDomain
    : peopleDomain;

  // console.debug("contactAddress", site, social_media, brochures, sec_nav_title);
  const menuItems = footerMenu?.menuItems?.edges?.flatMap(
    (menuItem) => menuItem.node
  );

  const restructureMenu = flattenMenu(menuItems, { idKey: "id" });
  const footerTreeMenu = [];

  restructureMenu?.forEach((menuItem, i) => {
    const path = menuItem.path ?? menuItem.url;
    footerTreeMenu.push({
      ...menuItem,
      title: menuItem.label,
      url: path
    });
  });

  // const banner = useBannerManager();
  const getBannerConfig = useBannerDetails(banner, "mobile");
  const programFees = useProgramFees();

  const floatCardGrantsTitle = shortcodePreProcessor({
    programFees,
    currentSiteMeta,
    description: getBannerConfig?.title
  });

  const floatCardGrantsDesc = shortcodePreProcessor({
    programFees,
    currentSiteMeta,
    description: getBannerConfig?.desc
  });

  useEffect(() => {
    const appFormSlug = [
      "enquiry-form",
      "application-form",
      "thank-you-for-your-application",
      "thank-you-for-your-complete-application",
      "group-application",
      "group-application-form",
      "secure-your-spot",
      "opendayssign-up",
      "thank-you-for-your-application/enrol",
      "enrol",
      "outreach-form",
      "pay-online"
    ];

    const currentPath = window !== undefined ? window.location.pathname : "";
    setIsFormPage(appFormSlug.includes(currentPath));

    const path = currentPath.split("/").filter((slug) => slug.trim() !== "");
    const lastSegment = path[path.length - 1];

    //set the url slug to the banner
    setBanner(lastSegment);

    setIsFormPage(appFormSlug.includes(lastSegment));
    // eslint-disable-next-line no-restricted-globals
    let theUrl = new URL(location);
    let groupInstitute = theUrl?.searchParams.get("group_institute");

    if (
      !theUrl?.pathname?.includes("corporate") &&
      theUrl?.pathname === "/group-travel/" &&
      (enquireNow.includes("group_enquiry") ||
        applyNow.includes("group_source"))
    ) {
      setApplyNowUrl(applyNow);
      setEnquireUrl(enquireNow);
    } else if (
      !theUrl?.pathname?.includes("corporate") &&
      theUrl?.pathname?.includes("group-travel")
    ) {
      if (
        enquireNow.includes("group_enquiry") ||
        applyNow.includes("group_source")
      ) {
        setApplyNowUrl(applyNow);
        setEnquireUrl(enquireNow);
      } else {
        let useAppURL = theUrl?.href?.includes("group-travel")
          ? `/group-application-form/${
              groupInstitute
                ? `?type=application&group_institute=${groupInstitute}`
                : "?type=application"
            }`
          : applyNow;

        let useEnquireURL = theUrl?.href?.includes("group-travel")
          ? `/enquiry-form/${
              groupInstitute
                ? `?group_institute=${groupInstitute}&type=group_enquiry`
                : "?type=group_enquiry"
            }`
          : enquireNow;

        setApplyNowUrl(useAppURL);
        setEnquireUrl(useEnquireURL);
      }

      setCurrentGroupTravelURL(true);
    } else {
      setApplyNowUrl(applyNow);
      setEnquireUrl(enquireNow);
    }
  }, [applyNow, enquireNow]);

  useEffect(() => {
    const nav = document.querySelector("#mobile-footer-cta");
    let lastScrollY = window.scrollY;

    if (updateOnScroll) {
      window.addEventListener("scroll", () => {
        if (lastScrollY < window.scrollY) {
          nav?.classList?.add("!hidden");
        } else {
          nav?.classList?.remove("!hidden");
        }

        lastScrollY = window.scrollY;
      });
    }

    const handleScroll = () => {
      if (stickyBarRef.current) {
        const heroBottom =
          stickyFooterDetails?.heroRef?.current?.getBoundingClientRect()
            .bottom || 500;

        if (heroBottom + 150 >= window.innerHeight) {
          stickyBarRef.current.classList.remove("sm:block");
        } else {
          stickyBarRef.current.classList.add("sm:block");
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
  }, []);

  const stickyBarRef = useRef(null);

  // useEffect(() => {
  //   //get current location
  //   const currentPath = window !== undefined ? window.location.pathname : "";
  //   const path = currentPath.split("/").filter((slug) => slug.trim() !== "");
  //   const lastSegment = path[path.length - 1];

  //   setBanner(lastSegment);
  // }, []);

  return (
    <>
      {footerTreeMenu.length > 0 && (
        <footer
          className={`flex justify-center w-full bg-yellow-standard dark:gvi-light-gray-bg py-14 ${updateOnScroll ? "pb-52 sm:pb-32 2xl:pb-48" : "pb-52 sm:pb-0"} ${additionalFooterContainerClass} ${isForm ? 'xxxs:pb-0' : ''} `}
        >
          <section className="container flex flex-wrap justify-center w-10/12 lg:flex-nowrap">
            <div className="flex flex-col w-full pr-0 sm:w-8/12 lg:w-4/12 sm:pr-14 lg:pr-20">
              <div className="flex items-center">
                <Image
                  imageAlt={"Logo"}
                  image={""}
                  imageClass="w-14"
                  sourceUrl={logo}
                />
                <a
                  href={`tel:${intlNumber}`}
                  className="flex items-center w-full text-white flex-inline"
                >
                  <PhoneIcon className="flex ml-auto w-7 h-7 sm:ml-9 sm:mr-2 fill-white" />
                  {intlNumber}
                </a>
              </div>

              <div className="mt-6">
                <DownloadButton
                  label="Download a brochure"
                  url={brochures.brochure_dl}
                />
              </div>
              <div className="mt-3">
                <DownloadButton
                  label="Download our parent pack"
                  url={globalFileLinks?.parentPack ?? brochures.parent_dl}
                />
              </div>
              <div className="mt-3">
                <DownloadButton
                  label="View our publications on "
                  url={social_media.google_scholar}
                  imgSource={GoogleScholarLogo}
                  imgClass="!px-2 mt-1"
                />
              </div>

              <p className="mt-6 text-xs font-normal text-white">
                Switch to the GVI {sec_nav_title ?? ""} site:
              </p>
              <a
                className="flex items-center pt-4 pb-7 sm:pb-0 sm:pt-0 sm:h-full"
                href={secondarySiteUrl ?? "#"}
              >
                <Image
                  imageAlt={"Logo"}
                  image={""}
                  imageClass="w-10"
                  sourceUrl={logo}
                />
                <span className="px-4 text-xs font-semibold text-white uppercase menu-link tracking-[0.19em]">
                  {sec_nav_title ?? ""}
                </span>
                <ArrowRight className="stroke-green-standard dark:stroke-red-light" />
              </a>
            </div>
            <div className="w-full sm:w-4/12 lg:w-8/12 grid grid-cols-none sm:grid-cols-4 gap-0">
              <div className="w-full">
                <SubAccordionList data={footerTreeMenu[0]} />
              </div>
              <div className="w-full">
                <SubAccordionList
                  data={footerTreeMenu[1] ?? footerTreeMenu[0]}
                />
                <SubAccordionList
                  data={footerTreeMenu[2] ?? footerTreeMenu[0]}
                  className="mt-0 sm:mt-6"
                />
              </div>
              <div className="w-full">
                <SubAccordionList
                  data={footerTreeMenu[3] ?? footerTreeMenu[0]}
                />
                <SubAccordionList
                  data={footerTreeMenu[4] ?? footerTreeMenu[0]}
                  className="mt-0 sm:mt-6"
                />
              </div>
              <div className="w-full">
                <SubAccordionList
                  data={footerTreeMenu[5] ?? footerTreeMenu[0]}
                />
                <BadgeofEthicsSvg className="mt-6" />
              </div>
              <div className="col-span-full grid sm:grid-cols-2 gap-3 pt-3 sm:gap-0 sm:items-center sm:py-6">
                <div className="w-full col-span-1">
                  <div className="flex justify-between sm:justify-start">
                    <SocialLink
                      url={social_media?.facebook}
                      icon={
                        <FacebookIcon className="m-auto fill-yellow-standard dark:fill-white" />
                      }
                      altName="Facebook"
                    />

                    <SocialLink
                      url={social_media?.instagram}
                      icon={
                        <InstaIcon className="m-auto fill-yellow-standard dark:fill-white" />
                      }
                    />
                    <SocialLink
                      url={social_media?.youtube}
                      icon={
                        <YoutubeIcon className="m-auto fill-yellow-standard dark:fill-white" />
                      }
                    />
                    {/* <SocialLink
                      url={social_media?.twitter}
                      icon={
                        <TweetIcon className="m-auto fill-yellow-standard dark:fill-white" />
                      }
                    /> */}
                    <SocialLink
                      url={social_media?.tiktok}
                      icon={
                        <TiktokIcon className="m-auto fill-yellow-standard dark:fill-white" />
                      }
                    />
                    <SocialLink
                      url={social_media?.linkedin}
                      icon={
                        <LinkIcon className="m-auto fill-yellow-standard dark:fill-white" />
                      }
                    />
                  </div>
                </div>
                <div className="w-full col-span-1 text-xs text-white dark:text-gray-darken">
                  {contactAddress && (
                    <>
                      <strong className="text-white">Physical address:</strong>
                      {contactAddress}
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>
        </footer>
      )}

      {!isForm && showMobileStickyFooter && (
        <div
          id={"mobile-footer-cta"}
          className={`fixed inset-x-0 bottom-0 flex flex-col place-items-center justify-center items-center bg-white dark:gvi-black-menu border-t border-white z-30 px-5 py-3 ${
            !isMobile ? "block sm:hidden" : ""
          }`}
        >
          {showGrantsBanner && (
            <div className="w-full py-4 flex flex-col gap-2">
              <div
                className="text-black-lighter dark:text-white text-base font-bold"
                dangerouslySetInnerHTML={{ __html: floatCardGrantsTitle }}
              />
              <div
                className="text-black-lighter dark:text-white text-sm"
                dangerouslySetInnerHTML={{ __html: floatCardGrantsDesc }}
              />
            </div>
          )}
          {showPricingStickyWidget &&
            programType.includes("volunteering") &&
            showProgramPrice &&
            programPrice?.length > 0 && (
              <div
                className="text-black-lighter dark:text-white flex-col pb-2 text-xl font-body font-semibold"
                dangerouslySetInnerHTML={{ __html: `From ${programPrice}` }}
              />
            )}

          {showPromotion && programPromotion?.length > 0 && !isMobile && (
            <div
              className="text-black-lighter dark:text-white flex-col pb-2 text-mob font-body font-normal"
              dangerouslySetInnerHTML={{
                __html: `${programPromotion}`
              }}
            />
          )}

          <div className="menu-buttons flex w-full grow-[1.5] gap-3">
            {enquireUrl?.length < 2 && applyNowUrl?.length < 2 && (
              <div className="w-full block" />
            )}

            {showMobileEnquireNow && enquireUrl?.length > 2 && (
              <div className="items-center bg-yellow-standard hover:bg-yellow-light hover:gvi-box-shadow dark:gvi-gray-bg justify-center grow basis-0 cursor-pointer xl:flex dark:hover:bg-gray-darkest rounded-xxs">
                <Link
                  className={`flex justify-center items-center py-2 ${
                    currentGroupTravelURL
                      ? "capitalize text-[16px]"
                      : "uppercase"
                  } h5 font-semibold !text-white px-4`}
                  to={enquireUrl}
                >
                  <div className="col-span-1">
                    {currentGroupTravelURL ? "Educator Enquiry" : "enquire"}
                  </div>
                  <div className="col-span-1 hidden sm:block">
                    <ArrowRight className="stroke-white w-4 h-4" />
                  </div>
                </Link>
              </div>
            )}

            {showMobileApplyNow && applyNowUrl?.length > 2 && (
              <div className="items-center bg-red-light hover:bg-black-lighter hover:gvi-box-shadow dark:gvi-bg-green justify-center grow basis-0 cursor-pointer xl:flex dark:hover:bg-white rounded-xxs px-4">
                <Link
                  className={`flex justify-center items-center py-2 ${
                    currentGroupTravelURL
                      ? "capitalize text-[16px]"
                      : "uppercase"
                  } h5 !text-white dark:!text-black`}
                  to={applyNowUrl}
                >
                  <div className="col-span-6">
                    {currentGroupTravelURL ? "Student Application" : "apply"}
                  </div>
                  <div className="col-span-1 hidden sm:block">
                    <ArrowRight className="stroke-white dark:stroke-gray-darker w-4 h-4" />
                  </div>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {!isForm &&
        showMobileStickyFooter &&
        stickyFooterDetails &&
        Object.keys(stickyFooterDetails).length > 0 && (
          <div
            ref={stickyBarRef}
            className="hidden fixed bottom-0 left-0 w-full dark:bg-white dark:bg-opacity-20 bg-gray-lighten bg-opacity-60 z-30 backdrop-blur-[20px]"
          >
            <BlogStickyFooter {...stickyFooterDetails} />
          </div>
        )}

      <SiteSwitcherPopup />
    </>
  );
};

export default Footer;
